import { GlobalCopilotFloatieButton } from '@ms/yammer-copilot/dist/integration';
import {
  LeftNavCollapseDisabledProvider,
  ResizableLeftNavProvider,
  useIsLeftNavCollapseEnabled,
  useIsLeftNavCollapsedActive,
} from '@ms/yammer-features-app-navigation';
import { useIsEngageIASimplificationPhase1WithoutReporting } from '@ms/yammer-features-engage-ia';
import { useExperimentTreatmentWithoutReporting } from '@ms/yammer-hooks/dist/experiments';
import { useIsTopLevelFeaturePage } from '@ms/yammer-hooks/dist/navigation';
import { useTranslations } from '@ms/yammer-i18n-react';
import { useEndActiveSession } from '@ms/yammer-search-components';
import CommonStrings from '@ms/yammer-strings/dist/Common';
import PanesStrings from '@ms/yammer-strings/dist/Panes';
import { useDiagnosticsPanelMenuButtonItem } from '@ms/yammer-web-components/dist/teams/VivaEngageNavigationHeaderMoreActions/hooks';
import { MultiColumnAppLayout } from '@ms/yammer-web-core-ui/dist/components/AppLayout';
import { AppLayoutBreakpoint, useContainerWidth } from '@ms/yammer-web-core-ui/dist/util/responsiveness';
import { tokens } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import {
  articleBasePath,
  articleEditPageMatchPath,
  articleNewMatchPath,
  articlePageMatchPath,
  userPendingPostArticlePath,
} from '@ms/yammer-web-support/dist/urls';
import { useUIFeatures } from '@ms/yammer-web-ui-features/dist/features';
import { useTeamsClientContext } from 'components/TeamsClientProvider/hooks';
import type { FC, PropsWithChildren } from 'react';
import React from 'react';
import { useRouteMatch } from 'react-router-dom';

import { UserProfileMenuButton } from '../UserProfileMenuButton';

import { AppLeftNav } from './AppLeftNav';
import LoadableAppLeftNav from './LoadableAppLeftNav';
import ArticleFooter from './LoadableArticleFooter';
import LoadableTeamsSubHeader from './LoadableTeamsSubHeader';
import { useBackButtonProps, useGetBanners } from './hooks';

const AppLayout: FC<PropsWithChildren> = ({ children }) => {
  useEndActiveSession();
  /**
   * This custom Article logic is temporary until we refactor CoreUI's AppLayout so
   * content/page-level templates allow slot props like `footer`.
   * https://dev.azure.com/yammer/engineering/_workitems/edit/295328
   */
  const isPendingPostArticlePage = !!useRouteMatch({ path: userPendingPostArticlePath, exact: true });
  const isAnyArticlesPage = !!useRouteMatch(articleBasePath) || isPendingPostArticlePage;
  const isArticleCreatePage = !!useRouteMatch({ path: articleNewMatchPath, exact: true });
  const isArticleEditPage = !!useRouteMatch(articleEditPageMatchPath);
  const isArticleThreadPage = !!useRouteMatch(articlePageMatchPath) && !isArticleEditPage && !isArticleCreatePage;
  const isSuggestedArticlePage = !!useRouteMatch({ path: '/groups/:groupId/suggested-article', exact: true });
  const isArticleAuthorHeaderEnabled = useExperimentTreatmentWithoutReporting('ArticleModalDialog');
  const isArticlePage = !!useRouteMatch(articlePageMatchPath) || isPendingPostArticlePage;

  const backgroundColor = isAnyArticlesPage || isSuggestedArticlePage ? tokens.colorNeutralBackground1 : undefined;
  const footer = isArticleThreadPage || isPendingPostArticlePage ? <ArticleFooter /> : undefined;

  const width = useContainerWidth();
  const isCompact = width < AppLayoutBreakpoint.Width1032;

  const scrollPaddingTop =
    isArticleEditPage || isArticleCreatePage || (isArticleAuthorHeaderEnabled && isArticlePage) ? '120px' : '0px';
  const scrollPaddingBottom = (isArticleEditPage || isArticleCreatePage) && !isCompact ? '113px' : '0px';

  const { shouldHideSubHeader } = useTeamsClientContext();
  const subHeader = !shouldHideSubHeader ? <LoadableTeamsSubHeader /> : null;

  const isEngageIASimplification = useIsEngageIASimplificationPhase1WithoutReporting();
  const isLeftNavCollapsed = useIsLeftNavCollapsedActive();
  const isLeftNavCollapsible = useIsLeftNavCollapseEnabled();
  const banners = useGetBanners();

  const isLeftSidebarEnabled = useUIFeatures((features) => features.navigation?.leftSidebar);
  const { item: diagnosticsPanelMenuButtonItem, content: diagnosticsPanel } = useDiagnosticsPanelMenuButtonItem();
  const userProfileLinkActionButtons = [
    <UserProfileMenuButton
      key="UserProfileMenuButton"
      diagnosticsPanelMenuButtonItem={diagnosticsPanelMenuButtonItem}
    />,
  ];

  const leftNav = isLeftSidebarEnabled ? (
    <AppLeftNav
      userProfileLinkActionButtons={userProfileLinkActionButtons}
      collapsedOverlayNav={
        <LeftNavCollapseDisabledProvider>
          <LoadableAppLeftNav userProfileLinkActionButtons={userProfileLinkActionButtons} />
        </LeftNavCollapseDisabledProvider>
      }
    />
  ) : null;

  const openAriaLabel = useTranslations(PanesStrings).showAdditionalInformationPane();
  const commonStrings = useTranslations(CommonStrings);
  const backButtonAriaLabel = commonStrings.commonGoBack();
  const closeButtonAriaLabel = commonStrings.commonClose();

  const isTopLevelFeaturePage = useIsTopLevelFeaturePage();
  const { goBack, showButton: hasBackButton } = useBackButtonProps();

  return (
    <>
      <ResizableLeftNavProvider>
        <MultiColumnAppLayout
          header={<GlobalCopilotFloatieButton />}
          headerDepth="depth0"
          headerBackgroundColor={tokens.colorNeutralBackground2}
          subHeader={subHeader}
          banner={banners}
          backButtonAriaLabel={backButtonAriaLabel}
          hasBackButton={hasBackButton}
          onBackButtonClick={goBack}
          leftNav={leftNav}
          hasHeroBackground={isTopLevelFeaturePage}
          openContentSidebarAriaLabel={openAriaLabel}
          closeContentSidebarAriaLabel={closeButtonAriaLabel}
          footer={footer}
          backgroundColor={backgroundColor}
          scrollPaddingTop={scrollPaddingTop}
          scrollPaddingBottom={scrollPaddingBottom}
          unifiedSidebar={isEngageIASimplification && !isAnyArticlesPage && !isSuggestedArticlePage}
          reservedRightRailBottomSpace={100}
          isLeftNavCollapsed={isLeftNavCollapsed}
          isLeftNavCollapsible={isLeftNavCollapsible}
        >
          {children}
        </MultiColumnAppLayout>
      </ResizableLeftNavProvider>
      {diagnosticsPanel}
    </>
  );
};

export default AppLayout;
